import { useEffect, useState } from "react";
import { Dialog } from "../../../../components/common/dialog";
import { SegmentedControl } from "../../../../components/common/segmented-control";
import { Text } from "../../../../components/common/typography";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { showErrorToast, showSuccessToast } from "../../../../utils/toasts";
import { adminCategoriesService } from "../../../api_client";
import {
  FIELD_KIND,
  FIELD_LABELS,
  KNOWN_FIELD_KEYS,
  policyToState,
  stateToPolicy,
  type FieldKey,
  type FieldState,
} from "../../../categories/data/field-config";

const STATE_OPTIONS: readonly { value: FieldState; label: string }[] = [
  { value: "hidden", label: "Hidden" },
  { value: "optional", label: "Optional" },
  { value: "required", label: "Required" },
];

interface Props {
  categoryId: number | null;
  categoryName: string;
  open: boolean;
  onClose: () => void;
  /** Resolve an ancestor category id to a display name for the inherited hint. */
  categoryNameById: (id: number) => string | undefined;
}

export function CategoryFieldConfigDialog({
  categoryId,
  categoryName,
  open,
  onClose,
  categoryNameById,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [states, setStates] = useState<Record<FieldKey, FieldState> | null>(null);
  const [initial, setInitial] = useState<Record<FieldKey, FieldState> | null>(null);
  const [ownKeys, setOwnKeys] = useState<Set<FieldKey>>(new Set());
  const [inheritedFrom, setInheritedFrom] = useState<Record<string, number | null>>({});

  useEffect(() => {
    if (!open || categoryId == null) return;
    let cancelled = false;
    setLoading(true);
    setStates(null);
    void (async () => {
      try {
        const { data } = await adminCategoriesService.adminGetCategoryFieldConfig(categoryId);
        if (cancelled) return;
        const next = {} as Record<FieldKey, FieldState>;
        for (const key of KNOWN_FIELD_KEYS) {
          const policy = data.effectiveConfig?.[key];
          next[key] = policy ? policyToState(policy) : "optional";
        }
        setStates(next);
        setInitial(next);
        setOwnKeys(new Set(Object.keys(data.ownConfig ?? {}) as FieldKey[]));
        setInheritedFrom(data.inheritedFrom ?? {});
      } catch (e) {
        showErrorToast((e as Error)?.message ?? "Failed to load field policy", e);
        onClose();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, categoryId, onClose]);

  const setFieldState = (key: FieldKey, value: FieldState) => {
    setStates((prev) => (prev ? { ...prev, [key]: value } : prev));
    // A manual change becomes an explicit own override (clears the inherited hint).
    setOwnKeys((prev) => new Set(prev).add(key));
  };

  const save = async () => {
    if (categoryId == null || !states || !initial) return;
    const config: Record<string, { visible: boolean; required: boolean }> = {};
    for (const key of KNOWN_FIELD_KEYS) {
      if (states[key] !== initial[key]) config[key] = stateToPolicy(states[key]);
    }
    if (Object.keys(config).length === 0) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      await adminCategoriesService.adminUpdateCategoryFieldConfig(categoryId, { config });
      showSuccessToast("Field policy updated");
      onClose();
    } catch (e) {
      showErrorToast((e as Error)?.message ?? "Save failed", e);
    } finally {
      setSaving(false);
    }
  };

  const inheritedHint = (key: FieldKey): string | null => {
    if (ownKeys.has(key)) return null;
    const fromId = inheritedFrom[key];
    if (fromId == null) return null;
    return `Inherited from ${categoryNameById(fromId) ?? `category #${fromId}`}`;
  };

  return (
    <Dialog
      open={open}
      setOpen={(o) => {
        if (!o) onClose();
      }}
      title={`Field policy — ${categoryName}`}
      showButtons
      buttonText={saving ? "Saving…" : "Save"}
      onConfirm={save}
      cancelText="Cancel"
      showCloseButton
      content={
        loading || !states ? (
          <ShimmerLoader message="Loading field policy…" lines={4} />
        ) : (
          <div className="flex flex-col gap-1 py-2">
            <Text variant="bodySm" tone="muted" className="mb-2">
              Choose which fields sellers see for this category. Settings inherit from
              the parent category until you override them here.
            </Text>
            {KNOWN_FIELD_KEYS.map((key) => {
              const hint = inheritedHint(key);
              return (
                <div
                  key={key}
                  className="flex items-center justify-between gap-4 border-b border-borderLight py-2 last:border-b-0"
                >
                  <div className="flex flex-col">
                    <Text variant="body" tone="dark">
                      {FIELD_LABELS[key]}
                      {FIELD_KIND[key] === "axis" ? " (variant option)" : ""}
                    </Text>
                    {hint && (
                      <Text variant="bodyXs" tone="muted">
                        {hint}
                      </Text>
                    )}
                  </div>
                  <SegmentedControl<FieldState>
                    options={STATE_OPTIONS}
                    value={states[key]}
                    onChange={(v) => setFieldState(key, v)}
                    aria-label={`${FIELD_LABELS[key]} visibility`}
                  />
                </div>
              );
            })}
          </div>
        )
      }
    />
  );
}
